import React, { useState } from 'react';
import { Shield, Users, Building2, FileCheck, HelpCircle, ChevronDown, ChevronRight, LayoutDashboard, FileText, UserPlus, CheckCircle2 } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { Modal } from '../../../components/ui/Modal';

interface UserGuideModalProps {
    isOpen: boolean;
    onClose: () => void;
}

type Tab = 'overview' | 'roles' | 'features' | 'faq';

export const UserGuideModal: React.FC<UserGuideModalProps> = ({ isOpen, onClose }) => {
    const { theme } = useTheme();
    const [activeTab, setActiveTab] = useState<Tab>('overview');
    const [openFaqQuestion, setOpenFaqQuestion] = useState<string | null>(null);

    const isDark = theme === 'dark';

    const tabs: { id: Tab; label: string; icon: React.ElementType }[] = [
        { id: 'overview', label: 'Overview', icon: HelpCircle },
        { id: 'roles', label: 'Roles', icon: Users },
        { id: 'features', label: 'Features', icon: LayoutDashboard },
        { id: 'faq', label: 'FAQ', icon: FileText },
    ];

    const faqs = [
        {
            question: "How do I verify an ESA letter?",
            answer: "Navigate to the Dashboard or Document Verification section. Upload the letter, and our system will automatically process and verify it against our database."
        },
        {
            question: "Can I add multiple properties?",
            answer: "Yes! Regional Managers and Company Owners can add unlimited properties. Go to the Side Menu and click 'Add Property'."
        },
        {
            question: "How do I invite team members?",
            answer: "Go to the 'Team Management' section in the Side Menu. Click 'Invite Member', enter their email, and assign a role."
        },
        {
            question: "What if a verification fails?",
            answer: "If a letter cannot be verified automatically, it will be flagged for manual review. You can then approve or reject it based on your own assessment."
        }
    ];

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="4xl"
            maxHeight="screen"
            hideHeader={true}
            bodyClassName="p-0 flex overflow-hidden"
            className="h-[80vh]"
            ariaLabelledBy="user-guide-modal-title"
        >
            {/* Sidebar Navigation */}
            <div className={`w-64 flex-shrink-0 p-6 flex flex-col border-r ${isDark ? 'border-storgia-light-blurple/10 bg-storgia-black/30' : 'border-gray-100 bg-gray-50/50'
                }`}>
                <div className="mb-8 flex items-center gap-3">
                    <div className={`p-2 rounded-xl ${isDark ? 'bg-storgia-blurple/20' : 'bg-indigo-100'}`}>
                        <HelpCircle className={`h-6 w-6 ${isDark ? 'text-storgia-blurple' : 'text-indigo-600'}`} aria-hidden="true" />
                    </div>
                    <h2 id="user-guide-modal-title" className={`text-xl font-bold font-league-spartan ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Guide
                    </h2>
                </div>

                <nav className="space-y-2 flex-1" aria-label="Guide sections">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            aria-current={activeTab === tab.id ? 'page' : undefined}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 font-medium font-league-spartan text-sm ${activeTab === tab.id
                                ? isDark
                                    ? 'bg-storgia-blurple text-white shadow-lg shadow-storgia-blurple/20'
                                    : 'bg-indigo-600 text-white shadow-lg shadow-indigo-200'
                                : isDark
                                    ? 'text-gray-400 hover:bg-white/5 hover:text-white'
                                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                                }`}
                        >
                            <tab.icon className={`h-5 w-5 ${activeTab === tab.id ? 'text-white' : ''}`} aria-hidden="true" />
                            {tab.label}
                        </button>
                    ))}
                </nav>

                <div className={`mt-auto pt-6 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                    <p className={`text-xs text-center mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        Version 2.1.0
                    </p>
                    <button
                        onClick={onClose}
                        aria-label="Close guide modal"
                        className={`w-full py-2.5 rounded-xl font-medium font-league-spartan text-sm transition-colors ${isDark
                            ? 'bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                    >
                        Close Guide
                    </button>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-8 relative">
                <div className="max-w-2xl mx-auto pt-2">

                    {/* OVERVIEW TAB */}
                    {activeTab === 'overview' && (
                        <div className="space-y-8 animate-fadeIn">
                            <div className="text-center mb-10">
                                <h1 className={`text-3xl font-bold font-league-spartan mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    Welcome to Authentipet
                                </h1>
                                <p className={`text-lg leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                    The complete platform for property managers to verify ESA letters, manage properties, and ensure compliance with ease.
                                </p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className={`p-6 rounded-2xl border ${isDark ? 'bg-storgia-black/40 border-storgia-light-blurple/20' : 'bg-indigo-50/50 border-indigo-100'}`}>
                                    <div className={`h-10 w-10 rounded-full flex items-center justify-center mb-4 ${isDark ? 'bg-storgia-green/20 text-storgia-green' : 'bg-emerald-100 text-emerald-600'}`}>
                                        <CheckCircle2 className="h-6 w-6" />
                                    </div>
                                    <h3 className={`font-bold font-league-spartan text-lg mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Fast Verification</h3>
                                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                        Automated checks against our comprehensive database of licensed professionals.
                                    </p>
                                </div>
                                <div className={`p-6 rounded-2xl border ${isDark ? 'bg-storgia-black/40 border-storgia-light-blurple/20' : 'bg-indigo-50/50 border-indigo-100'}`}>
                                    <div className={`h-10 w-10 rounded-full flex items-center justify-center mb-4 ${isDark ? 'bg-storgia-blurple/20 text-storgia-blurple' : 'bg-indigo-100 text-indigo-600'}`}>
                                        <Shield className="h-6 w-6" />
                                    </div>
                                    <h3 className={`font-bold font-league-spartan text-lg mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Secure & Compliant</h3>
                                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                        Built with security first to protect sensitive tenant and medical data.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ROLES TAB */}
                    {activeTab === 'roles' && (
                        <div className="space-y-6 animate-fadeIn">
                            <h2 className={`text-2xl font-bold font-league-spartan mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                User Roles
                            </h2>

                            <div className="space-y-4">
                                <div className={`p-5 rounded-2xl border transition-all hover:scale-[1.01] ${isDark ? 'bg-storgia-black/40 border-storgia-light-blurple/20' : 'bg-white border-gray-200 shadow-sm'}`}>
                                    <div className="flex items-start gap-4">
                                        <div className={`p-3 rounded-xl ${isDark ? 'bg-storgia-green/20' : 'bg-emerald-100'}`}>
                                            <Shield className={`h-6 w-6 ${isDark ? 'text-storgia-green' : 'text-emerald-600'}`} />
                                        </div>
                                        <div>
                                            <h3 className={`font-bold font-league-spartan text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>Super Admin</h3>
                                            <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                                Full system control. Can manage all companies, users, and system settings. Used for platform administration and support.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className={`p-5 rounded-2xl border transition-all hover:scale-[1.01] ${isDark ? 'bg-storgia-black/40 border-storgia-light-blurple/20' : 'bg-white border-gray-200 shadow-sm'}`}>
                                    <div className="flex items-start gap-4">
                                        <div className={`p-3 rounded-xl ${isDark ? 'bg-storgia-blurple/20' : 'bg-indigo-100'}`}>
                                            <Building2 className={`h-6 w-6 ${isDark ? 'text-storgia-blurple' : 'text-indigo-600'}`} />
                                        </div>
                                        <div>
                                            <h3 className={`font-bold font-league-spartan text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>Company Owner</h3>
                                            <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                                Head of a property management company. Can create properties, invite regional managers, and view analytics for the entire portfolio.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className={`p-5 rounded-2xl border transition-all hover:scale-[1.01] ${isDark ? 'bg-storgia-black/40 border-storgia-light-blurple/20' : 'bg-white border-gray-200 shadow-sm'}`}>
                                    <div className="flex items-start gap-4">
                                        <div className={`p-3 rounded-xl ${isDark ? 'bg-storgia-pink/20' : 'bg-pink-100'}`}>
                                            <Users className={`h-6 w-6 ${isDark ? 'text-storgia-pink' : 'text-pink-600'}`} />
                                        </div>
                                        <div>
                                            <h3 className={`font-bold font-league-spartan text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>Regional Manager</h3>
                                            <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                                Oversees a specific region or group of properties. Can add new properties and manage property managers within their region.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className={`p-5 rounded-2xl border transition-all hover:scale-[1.01] ${isDark ? 'bg-storgia-black/40 border-storgia-light-blurple/20' : 'bg-white border-gray-200 shadow-sm'}`}>
                                    <div className="flex items-start gap-4">
                                        <div className={`p-3 rounded-xl ${isDark ? 'bg-yellow-500/20' : 'bg-yellow-100'}`}>
                                            <FileCheck className={`h-6 w-6 ${isDark ? 'text-yellow-500' : 'text-yellow-600'}`} />
                                        </div>
                                        <div>
                                            <h3 className={`font-bold font-league-spartan text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>Property Manager</h3>
                                            <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                                Responsible for a single property. Handles day-to-day operations, reviews ESA submissions, and communicates with tenants.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* FEATURES TAB */}
                    {activeTab === 'features' && (
                        <div className="space-y-6 animate-fadeIn">
                            <h2 className={`text-2xl font-bold font-league-spartan mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                Platform Features
                            </h2>

                            <div className="grid gap-6">
                                <div className={`p-6 rounded-2xl border ${isDark ? 'bg-storgia-black/40 border-storgia-light-blurple/20' : 'bg-white border-gray-200'}`}>
                                    <div className="flex items-center gap-3 mb-3">
                                        <LayoutDashboard className={`h-6 w-6 ${isDark ? 'text-storgia-blurple' : 'text-indigo-600'}`} />
                                        <h3 className={`font-bold font-league-spartan text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>Smart Dashboard</h3>
                                    </div>
                                    <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                        Get a bird&apos;s-eye view of your entire operation. Track revenue, monitor active verifications, and see real-time status updates for all your properties in one place.
                                    </p>
                                </div>

                                <div className={`p-6 rounded-2xl border ${isDark ? 'bg-storgia-black/40 border-storgia-light-blurple/20' : 'bg-white border-gray-200'}`}>
                                    <div className="flex items-center gap-3 mb-3">
                                        <FileCheck className={`h-6 w-6 ${isDark ? 'text-storgia-green' : 'text-emerald-600'}`} />
                                        <h3 className={`font-bold font-league-spartan text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>Document Verification</h3>
                                    </div>
                                    <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                        Upload ESA letters directly to the platform. Our system analyzes the document, checks provider credentials, and returns a verification status within seconds.
                                    </p>
                                </div>

                                <div className={`p-6 rounded-2xl border ${isDark ? 'bg-storgia-black/40 border-storgia-light-blurple/20' : 'bg-white border-gray-200'}`}>
                                    <div className="flex items-center gap-3 mb-3">
                                        <UserPlus className={`h-6 w-6 ${isDark ? 'text-storgia-pink' : 'text-pink-600'}`} />
                                        <h3 className={`font-bold font-league-spartan text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>Team Management</h3>
                                    </div>
                                    <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                        Scale your team effortlessly. Invite new members via email, assign specific roles, and manage permissions to ensure everyone has the right access.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* FAQ TAB */}
                    {activeTab === 'faq' && (
                        <div className="space-y-6 animate-fadeIn">
                            <h2 className={`text-2xl font-bold font-league-spartan mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                Frequently Asked Questions
                            </h2>

                            <div className="space-y-3">
                                {faqs.map((faq) => (
                                    <div
                                        key={faq.question}
                                        className={`rounded-2xl border overflow-hidden transition-all ${isDark
                                            ? 'bg-storgia-black/40 border-storgia-light-blurple/20'
                                            : 'bg-white border-gray-200'
                                            }`}
                                    >
                                        <button
                                            onClick={() => setOpenFaqQuestion(openFaqQuestion === faq.question ? null : faq.question)}
                                            className="w-full flex items-center justify-between p-5 text-left"
                                        >
                                            <span className={`font-bold font-league-spartan ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                {faq.question}
                                            </span>
                                            {openFaqQuestion === faq.question ? (
                                                <ChevronDown className={`h-5 w-5 ${isDark ? 'text-storgia-blurple' : 'text-indigo-600'}`} />
                                            ) : (
                                                <ChevronRight className={`h-5 w-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                                            )}
                                        </button>

                                        <div
                                            className={`transition-all duration-300 ease-in-out overflow-hidden ${openFaqQuestion === faq.question ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
                                                }`}
                                        >
                                            <div className={`p-5 pt-0 text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                                {faq.answer}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className={`mt-8 p-6 rounded-2xl text-center ${isDark ? 'bg-storgia-blurple/10' : 'bg-indigo-50'}`}>
                                <p className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    Still have questions?
                                </p>
                                <a
                                    href="mailto:support@authentipet.com"
                                    className={`text-sm font-bold underline ${isDark ? 'text-storgia-light-blurple hover:text-white' : 'text-indigo-600 hover:text-indigo-800'}`}
                                >
                                    Contact Support
                                </a>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </Modal>
    );
};
